// Auto-Pause Rules Management Page (S010) - With Madgicx-Inspired Modal + P2/P3 Enhancements
import React, { useState, useEffect } from 'react';
import { autoPauseApi } from '../lib/autoPauseApi';
import { AutoPauseRuleCard } from '../components/AutoPauseRuleCard';
import { CreateAutoPauseRuleModal } from '../components/modals/CreateAutoPauseRuleModal';
import { AutoPausePerformanceDashboard } from '../components/AutoPausePerformanceDashboard';
import type { AutoPauseRule } from '../types/auto-pause';

export const AutoPauseRulesPage: React.FC = () => {
  const [rules, setRules] = useState<AutoPauseRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoPauseRule | undefined>(undefined);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await autoPauseApi.list();
      setRules(data);
      setError(null);
    } catch (err) {
      setError('Failed to load auto-pause rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await autoPauseApi.toggle(id, isActive);
      setRules(rules.map(r => r.id === id ? { ...r, enabled: isActive } : r));
    } catch (err) {
      alert('Failed to toggle rule');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await autoPauseApi.delete(id);
      setRules(rules.filter(r => r.id !== id));
    } catch (err) {
      alert('Failed to delete rule');
      console.error(err);
    }
  };

  const handleTest = async (id: string) => {
    try {
      const result = await autoPauseApi.test(id);
      alert(`Test complete: Would pause campaign based on current metrics`);
    } catch (err) {
      alert('Failed to test rule');
      console.error(err);
    }
  };

  const handleEdit = (rule: AutoPauseRule) => {
    setEditingRule(rule);
    setShowCreateModal(true);
  };

  const handleRuleCreated = () => {
    loadRules();
    setShowCreateModal(false);
    setEditingRule(undefined);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingRule(undefined);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading rules...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-black dark:text-white">
            Auto-Pause Rules
          </h1>
          <p className="text-bodydark dark:text-bodydark1 mt-2">
            Automatically pause campaigns when conditions are met
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{ 
            backgroundColor: '#3C50E0',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          <svg style={{ width: '20px', height: '20px', marginRight: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Rule
        </button>
      </div>

      {/* P2: Performance Dashboard */}
      {rules.length > 0 && <AutoPausePerformanceDashboard />}

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900 dark:bg-opacity-20 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      {rules.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark">
          <svg className="w-16 h-16 mx-auto text-bodydark dark:text-bodydark1 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-black dark:text-white mb-2">
            No Auto-Pause Rules
          </h3>
          <p className="text-bodydark dark:text-bodydark1 mb-4">
            Create your first rule to automatically pause underperforming campaigns
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ 
              backgroundColor: '#3C50E0',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Create Your First Rule
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map(rule => (
            <AutoPauseRuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onTest={handleTest}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Rule Modal */}
      <CreateAutoPauseRuleModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        onRuleCreated={handleRuleCreated}
        editRule={editingRule}
      />
    </div>
  );
};
